import type { ProjectChange } from '@shared/helpers/pipeline/update-engine.models';
import { ProjectChangeType } from '@shared/helpers/pipeline/update-engine.models';

const RECIPE_FIRST_CHANGE_TYPES: ReadonlySet<ProjectChangeType> = new Set([
  ProjectChangeType.ElementRecipeApply,
  ProjectChangeType.ElementRecipeDetach,
  ProjectChangeType.ElementRecipeClear,
]);

/** Changes that create or reshape the element tree; must apply before edits on new recipe-child ids. */
const PROPOSAL_STRUCTURE_CHANGE_TYPES: ReadonlySet<ProjectChangeType> = new Set(
  [
    ProjectChangeType.ElementRecipeApply,
    ProjectChangeType.ElementRecipeDetach,
    ProjectChangeType.ElementRecipeClear,
    ProjectChangeType.ElementAdd,
    ProjectChangeType.ElementRemove,
    ProjectChangeType.ElementActivate,
    ProjectChangeType.ElementDeactivate,
  ],
);

/**
 * Split condensed proposal changes so tree-shaping edits run before quantity/property
 * updates on ids that only exist after {@link ProjectChangeType.ElementRecipeApply}.
 */
export const splitProposalStructureChanges = (
  changes: readonly ProjectChange[],
): {
  structureChanges: ProjectChange[];
  followUpChanges: ProjectChange[];
} => {
  const structureChanges: ProjectChange[] = [];
  const followUpChanges: ProjectChange[] = [];
  for (const change of changes) {
    if (PROPOSAL_STRUCTURE_CHANGE_TYPES.has(change.type)) {
      structureChanges.push(change);
    } else {
      followUpChanges.push(change);
    }
  }
  return { structureChanges, followUpChanges };
};

/**
 * Orders proposal changes so structural recipe application runs before dependent
 * edits on recipe children (DEV-3293). Stable relative order within each group.
 */
export const sortProposalChangesWithRecipeFirst = (
  changes: readonly ProjectChange[],
): ProjectChange[] => {
  const recipeChanges: ProjectChange[] = [];
  const otherChanges: ProjectChange[] = [];
  for (const change of changes) {
    if (RECIPE_FIRST_CHANGE_TYPES.has(change.type)) {
      recipeChanges.push(change);
    } else {
      otherChanges.push(change);
    }
  }
  return [...recipeChanges, ...otherChanges];
};
